import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { StatusLabelComponent } from '../../../../shared/components/status-label/status-label.component';
import { Status } from '../../../../shared/models/status-label.model';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatButtonModule } from '@angular/material/button';
import { RedisCacheService } from '../../services/redis-cache.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { catchError, map, of, shareReplay, Subject, switchMap, takeUntil, throwError } from 'rxjs';
import { ReactiveFormsModule } from '@angular/forms';
import { StatusModalService } from '../../../../core/services/status-modal.service';
import { RedisCacheStatusEnum } from '../../models/redis-cache.models';
import { WebsocketService } from '../../../../core/services/websocket.service';
import { A11yModule } from '@angular/cdk/a11y';
import { WebsiteExtended } from '../../../../shared/models/website-overview.model';

@Component({
  selector: 'app-redis',
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    StatusLabelComponent,
    MatSlideToggleModule,
    FontAwesomeModule,
    MatButtonModule,
    A11yModule,
  ],
  templateUrl: './redis.component.html',
  styleUrl: './redis.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RedisComponent implements OnInit, OnDestroy {
  private redisCacheService = inject(RedisCacheService);

  private websocketService = inject(WebsocketService);

  private statusModalService = inject(StatusModalService);

  website = input.required<WebsiteExtended>();

  website$ = toObservable(this.website);

  cacheStatus$ = this.website$.pipe(
    switchMap(({ id }) =>
      this.redisCacheService.getRedisCacheStatusByWebsiteId(id).pipe(
        map(({ status }) => status),
        catchError((error) => {
          if (error.status === 404) {
            return of(RedisCacheStatusEnum.DISABLED);
          }

          return throwError(() => error);
        }),
      ),
    ),
    shareReplay(1),
  );

  destroyed$ = new Subject();

  cacheEnabled = signal(false);

  status = signal<Status>(Status.IN_PROGRESS);

  pending = computed(() => this.status() === Status.PENDING);

  syncing = signal(false);

  ngOnInit(): void {
    this.getStatus();

    this.websocketService
      .listen<{ payload: { status: RedisCacheStatusEnum } }>('redisCacheStatusUpdated')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((res) => {
        this.status.set(this.toStatus(res.payload.status));
        if (res.payload.status === RedisCacheStatusEnum.ENABLED) this.cacheEnabled.set(true);
        else this.cacheEnabled.set(false);
      });

    this.websocketService
      .listen<{ payload: { reason: string } }>('getRedisCacheStatusError')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((res) => {
        this.syncing.set(false);
        this.status.set(Status.ERROR);
        this.statusModalService.open({
          state: 'error',
          message: res.payload.reason,
        });
      });

    this.websocketService
      .listen<{ payload: { reason: string } }>('redisCacheStatusUpdateError')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((res) => {
        this.syncing.set(false);
        this.status.set(Status.ERROR);
        this.statusModalService.open({
          state: 'error',
          message: res.payload.reason,
        });
      });
  }

  getStatus(): void {
    this.cacheStatus$.subscribe({
      next: (status) => {
        this.cacheEnabled.set(status === RedisCacheStatusEnum.ENABLED);
        this.status.set(this.toStatus(status));
      },
      error: (error) => {
        this.statusModalService.open({
          state: 'error',
          message: error.error?.error,
        });
      },
    });
  }

  syncStatus(): void {
    this.syncing.set(true);
    this.redisCacheService.getRedisCacheSyncedStatusByWebsiteId(this.website().id).subscribe();
  }

  onCacheToggleChange(event: MatSlideToggleChange): void {
    event.source.toggle(); // To prevent visual toggle
    this.status.set(Status.PENDING);
    const observer = {
      error: (error: { error: { error?: string } }) => {
        this.statusModalService.open({
          state: 'error',
          message: error.error?.error || 'Error occurred',
        });
      },
    };
    if (this.cacheEnabled())
      this.redisCacheService.disableRedisCacheByWebsiteId(this.website().id).subscribe(observer);
    else this.redisCacheService.enableRedisCacheByWebsiteId(this.website().id).subscribe(observer);
  }

  toStatus(key: RedisCacheStatusEnum): Status {
    switch (key) {
      case RedisCacheStatusEnum.ENABLED:
        return Status.ENABLED;
      case RedisCacheStatusEnum.DISABLED:
        return Status.DISABLED;
      case RedisCacheStatusEnum.PENDING_ENABLE:
      case RedisCacheStatusEnum.PENDING_DISABLE:
        return Status.PENDING;
      default:
        return Status.ERROR;
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next('');
    this.destroyed$.complete();
  }
}
